import React, { useContext, useEffect, useState } from 'react';
import {
  CircularProgress,
  Typography,
  Stack,
  Grid,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip
} from '@mui/material';
import {
  getOwnershipTargetMetrics,
  saveTargetMetricValues,
  addAck,
  removeAck
} from '../../targets/data';
import { ROLES } from '../../auth/role.js';
import RoleContext from '../../auth/RoleContext.js';
import UserContext from '../../auth/UserContext.js';
import { Box } from '@mui/system';
import { LoadingButton } from '@mui/lab';
import { InfoOutlined } from '@mui/icons-material';

const OwnershipTargetFacetMetrics = ({
  ownership,
  targetFacet,
  targetPeriod,
  editable = false
}) => {
  const roleDetails = useContext(RoleContext);
  const userDetails = useContext(UserContext);
  const { ownershipId } = ownership;
  const targetPeriodId = targetPeriod.id;

  const [metrics, setMetrics] = useState([]);
  const [originalMetrics, setOriginalMetrics] = useState([]);
  const [targetPeriodAck, setTargetPeriodAck] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [acking, setAcking] = useState(false);
  const [remAcking, setRemAcking] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  // Dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmData, setConfirmData] = useState({}); // { type: 'removeAck'|'save', ackType?: 'internal'|'external' }

  useEffect(() => {
    const fetchMetrics = async () => {
      if (!ownershipId || !targetFacet?.id || !targetPeriodId) return;
      setLoading(true);
      try {
        const { targetMetrics, targetMetricValues, targetPeriodAck } = await getOwnershipTargetMetrics(
          targetPeriodId,
          targetFacet.id,
          ownershipId
        );

        const merged = targetMetricValues.map((tmv) => {
          const metricDef = targetMetrics.find((m) => m.id === tmv.targetMetricId);
          return {
            ...tmv,
            name: metricDef ? metricDef.name : '',
          };
        });

        setMetrics(merged);
        setOriginalMetrics(JSON.parse(JSON.stringify(merged)));
        setTargetPeriodAck(targetPeriodAck || null);
        setIsDirty(false);
      } catch (err) {
        console.error('Error loading target metrics', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [ownershipId, targetFacet, targetPeriodId]);

  useEffect(() => {
    setIsDirty(JSON.stringify(metrics) !== JSON.stringify(originalMetrics));
  }, [metrics, originalMetrics]);

  const handleValueChange = (index, val) => {
    const updated = [...metrics];
    updated[index].value = val === '' ? null : parseFloat(val);
    setMetrics(updated);
  };

  const handleCancel = () => {
    setErrorMessage('')
    setMetrics(JSON.parse(JSON.stringify(originalMetrics)));
    setIsDirty(false);
  };

  const handleSave = async () => {
    setSaving(true)
    try {
      await saveTargetMetricValues(metrics);
      setTargetPeriodAck()
      setOriginalMetrics(JSON.parse(JSON.stringify(metrics)));
      setIsDirty(false);
    } catch (err) {
      setErrorMessage(`Error saving target metric values: ${err}`);
    } finally {
      setSaving(false)
    }
  };

  const handleAddAck = async (ackType) => {
    setAcking(true)
    try {
      const updatedAck = await addAck(targetPeriod.id, targetFacet.id, ownershipId, ackType === 'internalLead');
      if (updatedAck.status === 'error') throw new Error(updatedAck.error);
      setTargetPeriodAck(updatedAck.instance);
    } catch (err) {
      console.error('Error adding acknowledgement', err);
    } finally {
      setAcking(false)
    }
  };

  const handleRemoveAck = async (ackType) => {
    setRemAcking(true)
    try {
      const updatedAck = await removeAck(targetPeriod.id, targetFacet.id, ownershipId, (ackType === 'internalLead'));
      if (updatedAck.status === 'error') throw new Error(updatedAck.error);
      setTargetPeriodAck(updatedAck.instance);
    } catch (err) {
      console.error('Error removing acknowledgement', err);
    } finally {
      setRemAcking(false)
    }
  };

  // Confirm dialog trigger
  const triggerConfirmRemoveAck = (ackType) => {
    setConfirmData({
      type: 'removeAck',
      ackType,
      message: `Are you sure you want to remove your acknowledgement of ${targetFacet.name} Targets?`
    });
    setConfirmOpen(true);
  };

  const triggerConfirmSave = () => {
    setErrorMessage('')
    if (targetPeriodAck?.internalKoid || targetPeriodAck?.internalLeadKoid || targetPeriodAck?.externalKoid) {
      setConfirmData({
        type: 'save',
        message: `WARNING: Updating values will remove acknowledgements for ${targetFacet.name} Targets`
      });
      setConfirmOpen(true);
    } else {
      handleSave();
    }
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    if (confirmData.type === 'removeAck') {
      handleRemoveAck(confirmData.ackType);
    }
    if (confirmData.type === 'save') {
      handleSave();
    }
  };

  if (loading) {
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <CircularProgress size={20} />
        <Typography variant="body1">
          Loading {targetFacet?.name} Targets...
        </Typography>
      </Stack>
    );
  }

  const formatUserName = (userObj, koId) => {
    return `${[(userObj?.firstName ?? null), (userObj?.lastName ?? null), `(${koId ?? userObj?.koId})`].filter((n) => n).join(' ')}`;
  }

  const formatLocaleDate = (date) => {
    const d = new Date(date);
    return [
      d.toLocaleDateString('en-US', { timeZone: 'America/New_York' }),
      d.toLocaleTimeString('en-US', { timeZone: 'America/New_York' }),
      '(ET)'
    ].join(' ');
  }

  const hasAllMetricValues = originalMetrics.length > 0 && originalMetrics.every(m => m.value !== null && m.value !== undefined)

  return (
    <Stack spacing={2}>
      <Grid container sx={{ borderTop: '1px solid #e0e0e0' }} pt={1}>
        {/* Metrics list */}
        <Grid item xs={5}>
          <Typography variant="body1" className='tis-form-title'>{targetFacet?.name} Targets</Typography>
          <Grid container spacing={2} p={2} alignItems="center">
            {metrics.map((metric, idx) => (
              <React.Fragment key={metric.targetMetricId}>
                <Grid item xs={5} justifyItems={'end'}>
                  <Stack direction='row'>
                    <Typography>{metric.name}</Typography>
                    { metric.description && metric.description.length > 0 &&
                      <Tooltip title={metric.description} arrow>
                        <InfoOutlined color="action" sx={{ height: '50%' }} />
                      </Tooltip>
                    }
                    <Typography>:</Typography>
                  </Stack>
                </Grid>
                <Grid item xs={7}>
                  {(editable && !!targetPeriod.active) ? (
                    <TextField
                      type="number"
                      value={metric.value ?? ''}
                      onChange={(e) => handleValueChange(idx, e.target.value)}
                      fullWidth
                      size="small"
                      onWheel={(e) => e.target.blur()}
                      InputProps={{
                        inputProps: { style: { MozAppearance: 'textfield' } },
                        sx: {
                          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                          },
                        },
                      }}
                    />
                  ) : (
                    <Typography>{metric.value !== null ? metric.value : ''}</Typography>
                  )}
                </Grid>
              </React.Fragment>
            ))}
            {(editable && !!targetPeriod.active) && (
              <>
                <Grid item xs={5}></Grid>
                <Grid item xs={7} justifyItems='center'>
                  {errorMessage !== '' && 
                    <Typography color='error'>{errorMessage}</Typography>
                  }
                  <Stack direction="row" spacing={2}>
                    <LoadingButton
                      variant="contained"
                      onClick={triggerConfirmSave}
                      disabled={!isDirty}
                      loading={saving}
                    >
                      Save
                    </LoadingButton>
                    <Button variant="outlined" onClick={handleCancel} disabled={!isDirty}>
                      Cancel
                    </Button>
                  </Stack>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>

        {/* Ack section */}
        <Grid item xs={6} alignContent={'center'}>
          { hasAllMetricValues &&
          <Box border="1px dashed #e0e0e0" borderRadius="16px" p={1}>
            <Typography className='tis-form-title' color='GrayText'>Acknowledgements</Typography>
            <Grid container p={1} rowGap={0.5}>
              <Grid item xs={12}>
                <Typography variant="body1" sx={{textDecoration: 'underline'}}>{ownership?.ownershipName}</Typography>
              </Grid>
              <Grid item xs={12} borderBottom='1px dotted #e0e0e0' pb={1}>
                <Stack direction="row" columnGap={1} paddingLeft={1} alignItems="center" justifyContent="space-between">
                  <Typography variant="body2" whiteSpace="pre-wrap">
                    {targetPeriodAck?.externalKoid
                      ? `${formatUserName(targetPeriodAck.ExternalUser, targetPeriodAck.externalKoid)}\n${formatLocaleDate(targetPeriodAck.externalDate)}`
                      : 'Not acknowledged yet'}
                  </Typography>
                  {(!!targetPeriod.active && targetPeriodAck?.externalKoid === userDetails.koId) &&
                    <LoadingButton
                      size="small"
                      variant="outlined"
                      color="secondary"
                      onClick={() => triggerConfirmRemoveAck('external')}
                      sx={{ fontSize: '0.6rem !important' }}
                      loading={remAcking}
                    >
                      Remove My Acknowledgement
                    </LoadingButton>
                  }
                  {(!!targetPeriod.active && !targetPeriodAck?.externalKoid
                    && roleDetails.hasRole([ROLES.BOTTLER_CORP])) && 
                    <LoadingButton
                      size="small"
                      variant="contained"
                      onClick={() => handleAddAck('external')}
                      sx={{ fontSize: '0.6rem !important' }}
                      loading={acking}
                    >
                      Acknowledge
                    </LoadingButton>
                  }
                </Stack>
              </Grid>

              <Grid item xs={12} pt={1}>
                <Typography variant="body1" sx={{textDecoration: 'underline'}}>NAOU QSE Lead</Typography>
              </Grid>
              <Grid item xs={12} borderBottom='1px dotted #e0e0e0' pb={1}>
                <Stack direction="row" columnGap={1} paddingLeft={1} alignItems="center" justifyContent="space-between">
                  <Typography variant="body2" whiteSpace="pre-wrap">
                    {targetPeriodAck?.internalKoid
                      ? `${formatUserName(targetPeriodAck.InternalUser, targetPeriodAck.internalKoid)}\n${formatLocaleDate(targetPeriodAck.internalDate)}`
                      : 'Not acknowledged yet'}
                  </Typography>
                  {(!!targetPeriod.active && targetPeriodAck?.internalKoid === userDetails.koId) &&
                    <LoadingButton
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={() => triggerConfirmRemoveAck('internal')}
                      sx={{ fontSize: '0.6rem !important' }}
                      loading={remAcking}
                    >
                      Remove My Acknowledgement
                    </LoadingButton>
                  }
                  {(!!targetPeriod.active && !targetPeriodAck?.internalKoid 
                    && (roleDetails.isQualityManager() || roleDetails.isSafetyManager())) &&
                    <LoadingButton
                      size="small"
                      variant="contained"
                      onClick={() => handleAddAck('internal')}
                      sx={{ fontSize: '0.6rem !important' }}
                      loading={acking}
                    >
                      Acknowledge
                    </LoadingButton>
                  }
                </Stack>
              </Grid>
              <Grid item xs={12} pt={1}>
                <Typography variant="body1" sx={{textDecoration: 'underline'}}>{targetFacet.name} Pillar Lead</Typography>
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" columnGap={1} paddingLeft={1} alignItems="center" justifyContent="space-between">
                  <Typography variant="body2" whiteSpace="pre-wrap">
                    {targetPeriodAck?.internalLeadKoid
                      ? `${formatUserName(targetPeriodAck.InternalLeadUser, targetPeriodAck.internalLeadKoid)}\n${formatLocaleDate(targetPeriodAck.internalLeadDate)}`
                      : 'Not acknowledged yet'}
                  </Typography>
                  {(!!targetPeriod.active && targetPeriodAck?.internalLeadKoid === userDetails.koId) &&
                    <LoadingButton
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={() => triggerConfirmRemoveAck('internalLead')}
                      sx={{ fontSize: '0.6rem !important' }}
                      loading={remAcking}
                    >
                      Remove My Acknowledgement
                    </LoadingButton>
                  }
                  {(!!targetPeriod.active && !targetPeriodAck?.internalLeadKoid 
                    && (roleDetails.isQualityManager() || roleDetails.isSafetyManager())) &&
                    <LoadingButton
                      size="small"
                      variant="contained"
                      onClick={() => handleAddAck('internalLead')}
                      sx={{ fontSize: '0.6rem !important' }}
                      loading={acking}
                    >
                      Acknowledge
                    </LoadingButton>
                  }
                </Stack>
              </Grid>
            </Grid>
          </Box>
          }
        </Grid>
      </Grid>

      {/* Confirm Dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
      >
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <DialogContentText variant='body1' color='HighlightText'>{confirmData.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirm} autoFocus>OK</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default OwnershipTargetFacetMetrics;